import React, { useEffect } from 'react'
import {
  CitationProgrammaticPageContextProvider,
  getCitationProgrammaticPageContextValue,
} from '@contexts/citation-programmatic-page-context'
import { PageContext } from '@contexts/page-context'
import {
  HomepageMobileExperiment,
  SignedInDesktopEditorPersistentClientCtaExperiment,
  TrialEligibilityCtaExperiment,
} from '@lib/experiment-patches'
import { BrowserPagesSignUpFlowExperiment } from '@lib/experiment-patches/browser-signup'
import { NinetailedProvider } from '@lib/ninetailed/providers'
import { NinetailedPreviewPluginData } from '@lib/api/ninetailed'
import {
  useBrowserService,
  useCommonServices,
  useExperimentService,
  usePageShow,
  useTrackingInit,
  useUserService,
} from '@lib/hooks'
import { withCurrentUser } from '@lib/mixins/with-current-user'
import { navHelper } from '@lib/services/navigation'
import { shouldEnableFullStory } from '@lib/tracking/fullstory'
import { TypePage } from '@lib/types'
import { PageThemeProvider } from '@contexts/page-theme-context'
import { LocalStorageProvider } from '@lib/utils/local-storage'
import { isEligibleGoogleOneTap } from '../google-one-tap/eligibility'
import { useIsInternalTesting } from '../ui-lib/hooks/use-internal-testing'
import { CmsPageBody } from './body'
import { CmsPageScripts } from './scripts'
import { getPageSlugFromFacade } from './utils'
import { setUtmParamsCookie } from '@components/form/utils/utm-params'
import { CmsI18nProvider } from '@lib/i18n/i18n-provider'

export interface CmsPageProps {
  pageStructure: TypePage
  ninetailedPreviewPluginData?: NinetailedPreviewPluginData
  isPreview?: boolean
  isStaticFallback?: boolean
}

export const CmsPage: React.FC<CmsPageProps> = withCurrentUser((props) => {
  const experimentService = useExperimentService()
  const userService = useUserService()
  const browserService = useBrowserService()
  const facadeService = useCommonServices().facadeService

  const { isPreview, isStaticFallback, pageStructure, ninetailedPreviewPluginData } = props

  const slug = getPageSlugFromFacade(useCommonServices().facadeService)

  // Apply experiments that modify page structure
  const patchedPageStructure: TypePage = [
    BrowserPagesSignUpFlowExperiment.patchPageStructure,
    HomepageMobileExperiment.patchPageStructure,
    TrialEligibilityCtaExperiment.patchPageStructure,
    SignedInDesktopEditorPersistentClientCtaExperiment.patchPageStructure,
  ].reduce((patchedPageStructure, expPatch) => {
    return expPatch(patchedPageStructure, experimentService, userService, browserService, facadeService, slug)
  }, pageStructure)

  const { isAnonymous } = userService.currentUser
  const { isChrome } = browserService

  const {
    pageId,
    reducedHeroPadding,
    isPageWidth1280 = false,
    useWdk2023Colors = false,
    useWdk2023Typography = false,
    isSectionScrollTrackingEnabled,
    isGoogleOneTapEnabled,
    isFullStoryEnabled,
    pageTemplate,
  } = patchedPageStructure.fields
  const {
    isGDPR,
    isUSA,
    isFullStoryEnabled: isFullStoryGateEnabled,
    isWebHeartbeatTrackingEnabled,
    isOneTrustDisabled,
    isSprigEnabled,
    isAirGapEnabled,
  } = experimentService
  const isInternalTesting = useIsInternalTesting()

  const showGoogleOneTap = isEligibleGoogleOneTap({
    isSSO: navHelper.currentQueryParams.is_sso === 'true',
    isAnonymous,
    isChrome,
    isGoogleOneTapEnabled,
    pageSlug: slug,
  })
  const enableFullStory = shouldEnableFullStory(!!isFullStoryEnabled, isFullStoryGateEnabled, isGDPR)

  useTrackingInit(pageId, slug)
  usePageShow(pageId, slug, isPreview, isStaticFallback)

  useEffect(setUtmParamsCookie, [])

  const isBrandRefreshTypography = !useWdk2023Typography
  const isBrandRefreshColors = !useWdk2023Colors

  return (
    <PageContext.Provider
      value={{
        pageId,
        slug,
        isPreview,
        isSectionScrollTrackingEnabled,
      }}
    >
      <CmsI18nProvider>
        <PageThemeProvider
          value={{
            pageWidth: isPageWidth1280 ? '1280' : '1080',
            navigation: '2024',
            color: isBrandRefreshColors ? 'convertTo2024' : 'asIs',
          }}
        >
          <CmsPageScripts
            isPreview={isPreview}
            enableOneTrust={!isOneTrustDisabled && (isGDPR || isUSA)}
            enableAirGap={isAirGapEnabled}
            showGoogleOneTap={showGoogleOneTap}
            enableFullStory={enableFullStory}
            enableGtmIntegration={!isInternalTesting}
            enableWebHeartbeat={isWebHeartbeatTrackingEnabled}
            enableSprig={isSprigEnabled}
          />
          <LocalStorageProvider>
            <CitationProgrammaticPageContextProvider
              value={getCitationProgrammaticPageContextValue(slug, pageTemplate)}
            >
              <NinetailedProvider ninetailedPreviewPluginData={ninetailedPreviewPluginData}>
                <CmsPageBody
                  data-font-stack={isBrandRefreshTypography ? 'glyph' : 'inter'}
                  pageFields={patchedPageStructure.fields}
                  showGoogleOneTap={showGoogleOneTap}
                  reducedHeroPadding={reducedHeroPadding}
                />
              </NinetailedProvider>
            </CitationProgrammaticPageContextProvider>
          </LocalStorageProvider>
        </PageThemeProvider>
      </CmsI18nProvider>
    </PageContext.Provider>
  )
})
